import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BarChartVariant from '../../components/BarChartVariant/BarChartVariant';
import MolstarViewer from '../../components/MolstarViewer/MolstarViewer';
import VariantTable from '../../components/VariantTable/VariantTable';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import { ProgressSpinner } from 'primereact/progressspinner';
import VariantSelect from '../../components/VariantSelect/VariantSelect';
import { getPDBsFromUniprot, structureMap } from '../../ApiFunctions';
import { ListBox } from 'primereact/listbox';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import ProteinLollipop from '../../components/ProteinLollipop/ProteinLollipop';
import complexData from '../../complex.json';
import complexMapping from '../../complex_mapping.json';
import { Dropdown } from 'primereact/dropdown';
import multimerData from '../../mapa_alphafold.json';

const stemOf = (f) => (f || '').replace(/-model_v\d+\.cif$/, '');

const VariantSection = ({ type, target, variantData, variantLoadData, activeTabIndex, datatable }) => {

  const [visibleVariant, setVisibleVariant] = useState(false);
  const [positionVariant, setPositionVariant] = useState('center');
  const [structurePDBIdVariant, setStructurePDBIdVariant] = useState(undefined);
  const [chain, setChain] = useState(undefined);
  const [uniqueVariantPdbIds, setUniqueVariantPdbIds] = useState([]);
  const [positionsVar, setPositionsVar] = useState([]);
  const [positionsVarPDB, setPositionsVarPDB] = useState([]);
  const [database, setDatabase]= useState("PDB");
  const [positionSummary, setPositionSummary] = useState();
  const [uniprotAccessionNumber, setUniprotAccessionNumber] = useState();
  const [loading, setLoading] = useState(false);
  const [isMappingStructure, setIsMappingStructure] = useState(false);
  const [filteredVariants, setFilteredVariants] = useState(variantData || []);

  // Filters Variant tab
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedFilterLoolip, setSelectedFilterLoolip] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [wasSelectedVariants, setWasSelectedVariants] = useState(false);
  const [itemsClinical, setItemsClinical] =  useState([]);
  const [itemsAAClass, setItemsAAClass] =  useState([]);
  const [itemsConsequence, setItemsConsequence] =  useState([]);
  const [annotation, setAnnotation] = useState("None");
  const [selectedConsequences, setSelectedConsequences] = useState([]);
  const [selectedClinicalSignificances, setSelectedClinicalSignificances] = useState([]);

  // --- AlphaFold multi-structure map (uniprot -> [{ url, file, chain, partner, type }]) ---
  const multimerMap = useMemo(() => multimerData.default || multimerData, []);
  const structureOptions = useMemo(() => {
    const entry = uniprotAccessionNumber ? multimerMap[uniprotAccessionNumber] : null;
    return Array.isArray(entry) ? entry : [];
  }, [uniprotAccessionNumber, multimerMap]);
  // For a map complex stem (AF-<16 digits>) return the target protein's chain.
  const mapChainFor = (pdbId) => {
    if (!pdbId || !/^AF-\d{16}$/i.test(pdbId)) return null;
    const rec = structureOptions.find(r => stemOf(r.file).toUpperCase() === String(pdbId).toUpperCase());
    return rec ? rec.chain : null;
  };
  // Dropdown options: label the map complexes as Homodimer / Heterodimer with X.
  const structureDropdownOptions = useMemo(() => (
    uniqueVariantPdbIds.map(id => {
      const rec = structureOptions.find(r => stemOf(r.file).toUpperCase() === String(id).toUpperCase());
      if (rec) {
        const label = rec.type === 'homodimer'
          ? `Homodimer (AF): ${id.toUpperCase()}`
          : `Heterodimer with ${rec.partner} (AF)`;
        return { label, value: id.toUpperCase() };
      }
      return { label: id.toUpperCase(), value: id.toUpperCase() };
    })
  ), [uniqueVariantPdbIds, structureOptions]);

const handleAnnotation = (e) => {
    setAnnotation(e.target.value);
};

  const groupedFilters = [{
                        label: 'Clinical Significance',
                        items: itemsClinical
                        },{
                         label: 'AlphaMissense class',
                         items: itemsAAClass
                        },{
                         label: 'Consequence',
                         items: itemsConsequence
                        }
                    ];

  const groupTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <div>{option.label}</div>
            </div>
        );
    };

 const showVariant = (positionVariant) => {
    setPositionVariant(positionVariant);
    setVisibleVariant(true);
  };

// Mapping variant onto 3D structure
const handleStructureMap = async (uniqueProteinPosition, value, uniprotAccessionNumber) => {
    setIsMappingStructure(true); // Set loading state TRUE
    setPositionsVar([]); // Clear previous positions (optional here, could be done before calling)
    setChain(undefined); // Clear previous chain

    try {
        const result = await structureMap(uniqueProteinPosition, value, uniprotAccessionNumber);

        // Check if result is undefined or missing expected properties
        if (!result || !result.mappedPositions || !result.chains) {
            setPositionsVar([]); // Ensure empty if mapping fails
            setChain(undefined);
            setIsMappingStructure(false); // Set loading state FALSE
            return; // Exit early
        }

        const { mappedPositions, chains } = result;

        // Check if either is empty - This might still mean valid mapping but no results
        // Keep the message logic separate from the mapping process itself
        setPositionsVar(mappedPositions || []); // Use empty array if undefined/null
        setChain(chains);

    } catch (error) {
        console.error("Error during structureMap call:", error);
        setPositionsVar([]); // Reset on error
        setChain(undefined);
    } finally {
        setIsMappingStructure(false); // Set loading state FALSE in finally block
    }
};


useEffect(() => {
    const fetchStructureData = async () => {
        try {
            if (!variantData || variantData.length === 0) {
                setUniprotAccessionNumber(null);
                setItemsClinical([]);
                setItemsAAClass([]);
                setItemsConsequence([]);
                setUniqueVariantPdbIds([]);
                setStructurePDBIdVariant(null);
                setPositionsVar([]);
                setChain(null);
                setDatabase(null);
                return;
            }

            const uniqueClinicalSignificance = [
                ...new Set(
                    variantData.flatMap(variant =>
                        variant.clinicalSignificance
                            .split(/[,/]/)
                            .map(val => val.trim())
                    )
                ),
            ];
            setItemsClinical(
                uniqueClinicalSignificance.map(significance => ({
                    label: significance,
                    value: significance + '_cli',
                }))
            );

            const uniqueAAClass = [...new Set(variantData.map(variant => variant.alphamissenseClass))];
            setItemsAAClass(
                uniqueAAClass.map(aaClass => ({
                    label: aaClass,
                    value: aaClass + '_aa',
                }))
            );

            const uniqueConsequence = [
                ...new Set(
                    variantData.flatMap(variant =>
                        variant.consequence
                            .split(/[,/]/)
                            .map(val => val.trim())
                    )
                ),
            ];
            setItemsConsequence(
                uniqueConsequence.map(consequence => ({
                    label: consequence,
                    value: consequence + '_cos',
                }))
            );

            const uniac = variantData[0]?.anPosition?.split("_")[0];
            setUniprotAccessionNumber(uniac);

            const uniqueProteinPosition = [
                ...new Set(variantData.map(variant => variant.proteinPosition)),
            ];

            if (uniac && uniqueProteinPosition.length > 0) {

                    const pdbList = await getPDBsFromUniprot(uniac);

                    const complexFiles = complexData
                        .filter(fileName => fileName.includes(uniac))
                        .map(fileName => fileName.replace('.cif', ''));

                    const mapComplexStems = Array.isArray(multimerMap[uniac])
                        ? multimerMap[uniac].map(r => stemOf(r.file)) : [];
                    const uniquePdbIds = [...new Set([`AF-${uniac}`,...pdbList, ...complexFiles, ...mapComplexStems])];

                    if (uniquePdbIds.length > 0) {
                        setUniqueVariantPdbIds(uniquePdbIds);

                        const initialPdbId = uniquePdbIds[0];
                        setStructurePDBIdVariant(initialPdbId);

                        const hyphenCount = (initialPdbId.match(/-/g) || []).length;

                        if (hyphenCount >= 2) {
                            setDatabase('CO');
                            setPositionsVar(uniqueProteinPosition);

                            const fileChains = complexMapping[initialPdbId + ".cif"];

                            let targetChain = "A";
                            if (fileChains) {
                                const foundChain = Object.keys(fileChains).find(key => fileChains[key] === uniac);
                                if (foundChain) {
                                    targetChain = foundChain;
                                }
                            }

                            setChain(new Array(uniqueProteinPosition.length).fill(targetChain));

                        } else if (initialPdbId.includes("AF-")) {
                            setDatabase('AF');
                            setPositionsVar(uniqueProteinPosition);
                            setChain(new Array(uniqueProteinPosition.length).fill("A"));
                        } else {
                            handleStructureMap(uniqueProteinPosition, initialPdbId, uniac);
                            setDatabase('PDB');
                        }
                    }
        }else {
                setUniqueVariantPdbIds([]);
                setStructurePDBIdVariant(null);
                setPositionsVar([]);
                setDatabase(null);
            }
        } catch (error) {
            console.error('Error fetching structure data:', error);
        }
    };

    fetchStructureData();
}, [variantData]);


// Show variant on the structure by click on table row
useEffect(() => {

    // Main logic
    if (selectedVariants.length > 0) {
        setPositionsVar([]);
        const uniquePositions = getUniquePositions(selectedVariants);

        setWasSelectedVariants(true);

        if (structurePDBIdVariant.includes("AF-")) {
            handleAFStructure(uniquePositions);
        } else if (!structurePDBIdVariant.includes("AF-")) {
            handlePDBStructure(uniquePositions);
        }

    } else if (selectedVariants.length === 0 && wasSelectedVariants === true) {

        const uniquePositions = getUniquePositions(variantData.map(variant => variant.proteinPosition));

        if (structurePDBIdVariant.includes("AF-")) {
              setPositionsVar(uniquePositions);
              setSelectedFilter([]);
              setDatabase("AF");
              setChain(new Array(uniquePositions.length).fill(mapChainFor(structurePDBIdVariant) || "A"));
        } else {
            handlePDBStructure(uniquePositions);
        }

    } else if (selectedVariants.length === 0) {
         setPositionsVar([]);
    }

}, [selectedVariants]);

const filterVariant = () => {
  setLoading(true);

  const proteinPositionsSet = new Set();

  if (!selectedFilter || selectedFilter.length === 0) {
    variantData.forEach(variant => proteinPositionsSet.add(variant.proteinPosition));
  } else {
    const consequenceFilters = [];
    const alphamissenseClassFilters = [];
    const clinicalSignificanceFilters = [];

    selectedFilter.forEach(filterValue => {
      const baseFilterValue = filterValue.replace(/(_aa|_cli|_cos)$/, '');

      if (filterValue.endsWith('_aa')) {
        alphamissenseClassFilters.push(baseFilterValue);
      } else if (filterValue.endsWith('_cli')) {
        clinicalSignificanceFilters.push(baseFilterValue);
      } else if (filterValue.endsWith('_cos')) {
        consequenceFilters.push(baseFilterValue);
      }
    });

    variantData.forEach(variant => {
      const matchesConsequence = consequenceFilters.length === 0 || consequenceFilters.some(filter =>
        variant.consequence && variant.consequence.includes(filter)
      );
      const matchesAlphamissenseClass = alphamissenseClassFilters.length === 0 || alphamissenseClassFilters.includes(variant.alphamissenseClass);
      const matchesClinicalSignificance = clinicalSignificanceFilters.length === 0 || clinicalSignificanceFilters.some(filter =>
        variant.clinicalSignificance && variant.clinicalSignificance.includes(filter)
      );

      if (matchesConsequence && matchesAlphamissenseClass && matchesClinicalSignificance) {
        proteinPositionsSet.add(variant.proteinPosition);
      }
    });
  }

  const uniqueProteinPositions = Array.from(proteinPositionsSet);

  if (database === "PDB") {
    handleStructureMap(uniqueProteinPositions, structurePDBIdVariant, uniprotAccessionNumber);
  } else if (database === "AF" || database === "CO" ) {
    setPositionsVar(uniqueProteinPositions);
  }

  setLoading(false);
};



const handleVariantPosition = (e) => {
     setPositionSummary(e.target.value);
};

const handlePDBidVariant = async (e) => {
    const value = e.target.value;
    setStructurePDBIdVariant(value);

    // Get protein position of variants
    const filteredData = variantData.map(variant => variant.proteinPosition);
    let uniqueProteinPosition = [...new Set(filteredData)];

    if (selectedVariants && selectedVariants.length > 0) {
        uniqueProteinPosition = [...new Set(selectedVariants)];
    }

    const hyphenCount = (value.match(/-/g) || []).length;

    if (hyphenCount >= 2) {
             setDatabase('CO');
             setPositionsVar(uniqueProteinPosition);

            const fileChains = complexMapping[value + ".cif"];

            let targetChain = "A";
            if (fileChains) {
                  const foundChain = Object.keys(fileChains).find(key => fileChains[key] === uniprotAccessionNumber);
                 if (foundChain) {
                    targetChain = foundChain;
                }
           }

             setChain(new Array(uniqueProteinPosition.length).fill(targetChain));
             setIsMappingStructure(false);

      } else if (value.includes("AF-")) {
         setPositionsVar(uniqueProteinPosition);
         setDatabase("AF");
         setChain(new Array(uniqueProteinPosition.length).fill(mapChainFor(value) || "A"));
         setIsMappingStructure(false); // Ensure mapping state is false for AF too
    } else if (!value.includes("AF-")){
        handleStructureMap(uniqueProteinPosition, value, uniprotAccessionNumber);
        setDatabase("PDB");
    }
};

const handleAFStructure = (uniquePositions) => {

        const hyphenCount = (structurePDBIdVariant.match(/-/g) || []).length;

        if (hyphenCount >= 2) {
             setDatabase('CO');
             setPositionsVar(uniquePositions);

            const fileChains = complexMapping[structurePDBIdVariant + ".cif"];

            let targetChain = "A"; // Default
            if (fileChains) {
                   const foundChain = Object.keys(fileChains).find(key => fileChains[key] === uniprotAccessionNumber);
                   if (foundChain) {
                      targetChain = foundChain;
                }
             }

             setChain(new Array(uniquePositions.length).fill(targetChain));

        } else  if (hyphenCount == 1){
             setPositionsVar(uniquePositions);
             setSelectedFilter([]);
             setDatabase("AF");
             setChain(new Array(uniquePositions.length).fill(mapChainFor(structurePDBIdVariant) || "A"));
        }
    };

const handlePDBStructure = async (uniquePositions) => {
        handleStructureMap(uniquePositions, structurePDBIdVariant, uniprotAccessionNumber)
        setDatabase("PDB");
        setSelectedFilter([]);
 };

const getUniquePositions = (variants) => [...new Set(variants)];

const filterVariantReset = () => {
       setSelectedFilter([]);

       // Mapping variant
        const filteredData = variantData.map(variant => variant.proteinPosition);
        const uniqueProteinPosition = [...new Set(filteredData)];

        if (database === "PDB") {
               handleStructureMap(uniqueProteinPosition, structurePDBIdVariant, uniprotAccessionNumber)
        } else {
            setPositionsVar(uniqueProteinPosition);
        }
        setLoading(false);

}

const matchedVariants = useMemo(() => {
    if (!variantData) return [];
    if (!selectedFilterLoolip || selectedFilterLoolip.length === 0) return variantData;

      const filterClean = []
      selectedFilterLoolip.forEach(filterValue => {
      const baseFilterValue = filterValue.replace(/(_aa|_cli|_cos)$/, '');

      if (filterValue.endsWith('_aa')) {
        filterClean.push(baseFilterValue);
      } else if (filterValue.endsWith('_cli')) {
        filterClean.push(baseFilterValue);
      } else if (filterValue.endsWith('_cos')) {
        filterClean.push(baseFilterValue);
      }
    });

    return variantData.filter((variant) => {
        return filterClean.some(filterItem => {
            const filterValue = (filterItem.name || filterItem.label || filterItem || "").toString().toLowerCase();
            const vConsequence = (variant.consequence || "").toLowerCase();
            const vClinSig = (variant.clinSig || "").toLowerCase();
            const vAA = (variant.alphamissenseClass || "").toLowerCase();

            return vConsequence.includes(filterValue) || vClinSig.includes(filterValue) || vAA.includes(filterValue) ;
        });
    });
}, [variantData, selectedFilterLoolip]);


useEffect(() => {
    setSelectedFilterLoolip([]);
    setFilteredVariants(variantData);
}, [variantData]);

const handleApplyFilter = () => {
    setFilteredVariants(matchedVariants);
};

const handleClearFilter = () => {
    setSelectedFilterLoolip([]);
    setFilteredVariants(variantData);
};

  return (
    <>
       <h2 className="display-6 fw-bold mb-5">ClinVar variants<sup><Button icon="pi pi-info"
               onClick={() => showVariant('top-right')} text size="small" className="btn-dialog"/></sup></h2>
               <Dialog header="Clinvar variants" visible={visibleVariant} position={positionVariant} style={{ width: '50vw' }}
               onHide={() => setVisibleVariant(false)}
                draggable={false} resizable={false}  breakpoints={{ '960px': '75vw', '641px': '100vw' }}>
                    <p className="m-0 mb-2 text-justify">
                      ClinVar variant annotations report the human variations classified for diseases for each residue.
                    </p>
                    <p>The variant annotation for each residue is presented in a table with 14 columns:</p>
                     <ul>
                    <li className="text-justify">The 'Position ' column indicates the residue position in the protein sequence.</li>
                    <li className="text-justify">The 'Location h19' column contains the location of the variants based on hg19.</li>
                    <li className="text-justify">The 'Location h38' column contains the location of the variants based on hg38.</li>
                    <li className="text-justify">The 'REF' column contains the reference allele.</li>
                    <li className="text-justify">The 'ATL' column contains the alternative allele.</li>
                    <li className="text-justify">The 'Variant ID' column contains the identifier(s) of co-located known variants.</li>
                    <li className="text-justify">The 'Consequence' column contains the consequence type.</li>
                    <li className="text-justify">The 'Clinical significance' column contains the ClinVar clinical significance of the dbSNP variant.</li>
                    <li className="text-justify">The 'Disease' column contains the ClinVar-associated diseases.</li>
                    <li className="text-justify">The 'Protein position' column contains the relative position of amino acid in protein.</li>
                    <li className="text-justify">The 'AA change' column contains the reference and variant amino acids.</li>
                    <li className="text-justify">The 'Codon' column contains the reference and variant codon sequence.</li>
                    <li className="text-justify">The 'AlphaMissense score' column contains the predicted score of AlphaMissense, an amino acid level predictor of pathogenicity.</li>
                    <li className="text-justify">The 'AlphaMissense class' column contains the predicted class of AlphaMissense, an amino acid level predictor of pathogenicity.</li>
                    <li className="text-justify">The 'ClinVar URL' column includes hyperlinks to specific variants on the ClinVar database.</li>
                    </ul>

                     <GraphlqlQuery type={type} target={target} tab={activeTabIndex}/>

                    <p className="m-0 mb-1 text-justify">For more information, please refer to the
                     <Link className="" to="/about/" target="_blank"><b> about</b></Link> or <Link className="" to="/api/" target="_blank"><b> API</b></Link> page.
                     </p>
                </Dialog>
        {variantLoadData &&  (
          <div className="row mb-4">
            <ProgressSpinner style={{width: '50px', height: '50px'}}
            strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
           </div>
        )}

        { (!variantLoadData) &&  (
        <div className="row mb-4">
            <VariantTable VariantData={variantData}  setPositionSummary={setPositionSummary}  setSelectedVariants={setSelectedVariants} setUniprotAccessionNumber={setUniprotAccessionNumber}  />
        </div>
        )}
        { (!variantLoadData && variantData.length > 0 && uniqueVariantPdbIds.length > 0 ) && (
         <div className="row">
              <h2 className="display-6 fw-bold mb-4">Structure</h2>
              { (positionsVar.length === 0 && selectedVariants.length !== 0 &&  !isMappingStructure)  && (
                  <div className="row mb-4">
                       <Message className="col-md-12 ml-left"
                       text="No variants were mapped to this structure due to the structure's lack of coverage of the sequence position." />
                  </div>
              )}
               { (positionsVar.length === 0 && selectedVariants.length === 0 && !isMappingStructure)  && (
                  <div className="row mb-4">
                       <Message className="col-md-12 ml-left"
                       text="No variants were mapped to this structure due to the structure's lack of coverage of the sequence position." />
                  </div>
              )}
                  <div className="col-md-2 mb-2">
                    <div className="bg-light rounded-3">
                      <div className="p-3">
                        <div className="mb-2">
                          <label htmlFor="structure" className="form-label">Structure&nbsp;</label>
                          <Dropdown
                                value={structurePDBIdVariant}
                                options={structureDropdownOptions}
                                onChange={(e) => handlePDBidVariant({ target: { value: e.value } })}
                                filter
                                placeholder="Select"
                                style={{ width: '100%' }}
                                className="structure-dropdown mb-3"
                              />
                          <div className="d-flex">
                            <div className="burble-variant"></div>
                            <p className="fs--2 text-justify">Variant position</p>
                           </div>
                           <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Filters&nbsp;</label>
                               <ListBox multiple value={selectedFilter} onChange={(e) => setSelectedFilter(e.value)}
                                options={groupedFilters} optionLabel="label"
                                optionGroupLabel="label" optionGroupChildren="items" optionGroupTemplate={groupTemplate}
                                className="w-full md:w-14rem fs--2 text-justify list-filter mb-2" listStyle={{ maxHeight: '100px'}}
                                disabled={selectedVariants.length > 0 }/>

                                <Button label="Filter" icon="pi pi-filter" className="fs--2 mb-1 me-1" loading={loading}
                                onClick={filterVariant} disabled={selectedVariants.length > 0 } />

                                <Button label="Clear filter" icon="pi pi-filter-slash" className="fs--2 mb-1"
                                onClick={filterVariantReset} disabled={selectedVariants.length > 0 }/>

                          </div>
                          <p className="fs--2 text-justify">Click on the viewer plugin icons to access more advanced features from
                          <Link target="_blank" to="https://molstar.org/viewer-docs/"> Mol*</Link></p>
                           <p className="fs--2 text-justify">*Some table variants cannot be mapped to the PDB structure due to the structure's
                            lack of coverage of the sequence position.</p>
                            <p className="fs--2 text-justify">*Select specific variants by clicking on the table row to view them in the structure view. Filtering is not available.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-10 mb-3">
                     <div className="viewer-box">
                           <MolstarViewer pdbId={structurePDBIdVariant} tab="variants" positions={positionsVar} chain={chain} database={database} uniac={uniprotAccessionNumber}/>
                      </div>
                  </div>
                 </div>
                    )}
              { (!variantLoadData && variantData.length > 0 && uniqueVariantPdbIds.length > 0) &&  (
              <div className="row">
                  <h2 className="display-6 fw-bold mb-4">Summary</h2>
                  { ( selectedVariants.length === 0 && !positionSummary )  && (
                     <p className="text-justify mb-3">Top 10 Residues Summary: This list ranks the top 10 residues based on the number of variants for each</p>
                  )}
                  { (selectedVariants.length === 1 || positionSummary)  && (
                     <p className="text-justify mb-3">Residues Summary: This ranks the five residue positions before and after the selected position, considering only those that contain reported variants</p>
                  )}
                  { selectedVariants.length > 1  && (
                     <p className="text-justify mb-3">Residues Summary: This shows the number of variants for each selected residue position</p>
                  )}
                  <div className="row">
                  <div className="col-md-2 mb-2">
                    <div className="bg-light rounded-3">
                      <div className="p-3">
                        <div className="mb-2">
                          <label htmlFor="structure" className="form-label">Variant position&nbsp;</label>
                            <VariantSelect  selectedVariants={selectedVariants}  variantData={variantData} positionSummary={positionSummary}  handleVariantPosition={handleVariantPosition} />
                          </div>
                      </div>
                    </div>
                  </div>
                  </div>
                   <div className="row">
                  <div className="col-md-6 mb-4">
                  <BarChartVariant title="Consequence" VariantData={variantData} consequence={itemsConsequence} clinical={itemsClinical} aa={itemsAAClass}  selectedVariants={selectedVariants} positionSummary={positionSummary}/>
                  </div>
                  <div className="col-md-6 mb-4">
                  <BarChartVariant title="Clinical Significance" VariantData={variantData} consequence={itemsConsequence} clinical={itemsClinical} aa={itemsAAClass} selectedVariants={selectedVariants} positionSummary={positionSummary}/>
                  </div>
                   </div>
                  <div className="row">
                  <div className="col-md-6">
                    <BarChartVariant title="Functional Impact" VariantData={variantData} consequence={itemsConsequence} clinical={itemsClinical} aa={itemsAAClass} selectedVariants={selectedVariants} positionSummary={positionSummary}/>
                  </div>
                  <div className="col-md-6 mb-5">
                  <BarChartVariant title="AlphaMissense class" VariantData={variantData} consequence={itemsConsequence} clinical={itemsClinical} aa={itemsAAClass} selectedVariants={selectedVariants} positionSummary={positionSummary}/>
                  </div>
                  </div>
                                   {(type === "gene" || type === "protein") &&  (
                  <div className="row">
                  <div className="col-md-2">
                  <div className="bg-light rounded-3">
                    <div className="px-4 py-4">
                        <div className="mb-2">
                            <label
                                    htmlFor="structure-color"
                                    className="form-label"
                            >
                                Annotation 
                            </label>
                            <select
                                    className="form-select mb-2"
                                    name="structure-annotation"
                                    value={annotation}
                                    onChange={handleAnnotation}
                            >
                                <option value="None">None</option>
                                <option value="unDomains"
                                 disabled={variantData?.every(residue => !residue.unDomains || residue.unDomains.trim() === "-")}
                                 >Domains</option>
                                 <option value="interPro" disabled={variantData?.every(residue => !residue.interproDomain || residue.interproDomain.trim() === "-")}>InterPro</option>
                                 <option value="pfamDomainPosHmm"
                                 disabled={variantData?.every(residue => !residue.pfamDomainPosHmm || residue.pfamDomainPosHmm.trim() === "-")}
                                 >PFAM-HMM Pos</option>
                            </select>
                             <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Filters&nbsp;</label>
                               <ListBox multiple value={selectedFilterLoolip} onChange={(e) => setSelectedFilterLoolip(e.value)}
                                options={groupedFilters} optionLabel="label"
                                optionGroupLabel="label" optionGroupChildren="items" optionGroupTemplate={groupTemplate}
                               className="w-full md:w-14rem fs--2 text-justify list-filter mb-2" listStyle={{ maxHeight: '100px'}}/>

                               <Button
                                        label="Filter"
                                        icon="pi pi-filter"
                                        className="fs--2 mb-1 me-1"
                                       onClick={handleApplyFilter}
                                    />
                              <Button
                                label="Clear filter"
                                icon="pi pi-filter-slash"
                                className="fs--2 mb-1"
                                onClick={handleClearFilter}
                            />

                          </div>
                       </div>
                   </div>
                   </div>
                   </div>

                   <div className="col-md-10">
                        <ProteinLollipop variants={filteredVariants} datatable={datatable}
                        annotations_type={annotation} activeTabIndex={activeTabIndex} />
                   </div>

                       </div>
                    )}
                   </div>
              )}
    </>
  );
};

export default VariantSection;